import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Card, CardBody, Typography } from "@material-tailwind/react";

import AlertMessage from "../../../common/actions/AlertMessage";
import Loader from "../../../common/loader/Loader";
import SectionTitle from "../../../common/shared/SectionTitle";
import { getSingleGrievance } from "../../../redux/actions/grievance_action";

const links = [
  { name: "Member Feedback & Grievances", link: "/home/grievances" },
  { name: "Tracking Details" },
];

const getStatusMeta = (status) => {
  switch (status) {
    case "pending":
      return {
        label: "pending",
        className: "bg-blue-50 text-blue-700",
      };
    case "in_progress":
      return {
        label: "in progress",
        className: "bg-amber-50 text-amber-700",
      };
    case "resolved":
      return {
        label: "resolved",
        className: "bg-green-50 text-green-700",
      };
    case "closed":
      return {
        label: "closed",
        className: "bg-red-50 text-red-700",
      };
    default:
      return {
        label: status?.replaceAll("_", " ") || "unknown",
        className: "bg-blue-gray-50 text-blue-gray-700",
      };
  }
};

const StatusBadge = ({ status }) => {
  const statusMeta = getStatusMeta(status);

  return (
    <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold capitalize ${statusMeta.className}`}>
      {statusMeta.label}
    </span>
  );
};

function GrievanceDetails() {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);

  const dispatch = useDispatch();
  const { currentGrievance, grievanceError } = useSelector(
    (state) => state.grievance
  );

  const fileBase =
    process.env.REACT_APP_FILES ||
    "https://gbafilesalt.blob.core.windows.net/storage";

  useEffect(() => {
    dispatch(getSingleGrievance(id)).finally(() => setLoading(false));
  }, [dispatch, id]);

  return (
    <>
      <SectionTitle
        title="Grievance Tracking"
        description="Review your submission and the latest follow-up updates from the executive team."
        children={links}
        link="/home/grievances"
      />

      <div className="my-8 max-w-[1200px] m-auto px-4 space-y-8">
        {grievanceError && <AlertMessage type="error" message={grievanceError} />}

        {loading ? (
          <Loader />
        ) : (
          <>
            <Card>
              <CardBody>
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div>
                    <Typography variant="h4" color="blue-gray">
                      {currentGrievance?.title}
                    </Typography>
                    <Typography className="mt-2 text-black">
                      {currentGrievance?.description}
                    </Typography>
                  </div>

                  {/* <StatusBadge status={currentGrievance?.status} /> */}
                </div>

                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-black">
                  <Typography className="capitalize">
                    <span className="font-bold">Category:</span> {currentGrievance?.category?.replaceAll("_", " ")}
                  </Typography>
                  {/* <Typography>
                    <span className="font-bold">Member:</span> {currentGrievance?.member_name || "Not supplied"}
                  </Typography> */}
                  <Typography>
                    <span className="font-bold">Practice Region:</span> {currentGrievance?.practice_region_name || "Not supplied"}
                  </Typography>
                  <Typography>
                    <span className="font-bold">Specific Court:</span> {currentGrievance?.court_name || "Not supplied"}
                  </Typography>
                  {/* <Typography>
                    <span className="font-bold">Contact Phone:</span> {currentGrievance?.contact_phone || "Not supplied"}
                  </Typography> */}
                  {/* <Typography>
                    <span className="font-bold">Contact Email:</span> {currentGrievance?.contact_email || "Not supplied"}
                  </Typography> */}
                  {/* <Typography>
                    <span className="font-bold">Bar Number:</span> {currentGrievance?.bar_number}
                  </Typography> */}
                  <Typography>
                    <span className="font-bold">Submitted:</span> {new Date(currentGrievance?.createdAt).toDateString()}
                  </Typography>
                  <Typography>
                    <span className="font-bold">Last Updated:</span> {new Date(currentGrievance?.updatedAt).toDateString()}
                  </Typography>
                </div>

                {currentGrievance?.attachment_file && (
                  <a
                    href={`${fileBase}/${currentGrievance.attachment_file}`}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-6 inline-block text-sm font-semibold text-blue-700"
                  >
                    View uploaded attachment
                  </a>
                )}
              </CardBody>
            </Card>

            <Card>
              <CardBody>
                <Typography variant="h5" color="blue-gray" className="mb-4">
                  Follow-up timeline
                </Typography>

                <div className="space-y-4">
                  {(currentGrievance?.updates || []).length === 0 ? (
                    <Typography className="text-sm text-blue-gray-600">
                      No follow-up updates yet.
                    </Typography>
                  ) : (
                    (currentGrievance?.updates || []).map((update) => (
                      <div
                        key={update.id}
                        className="rounded-xl border border-blue-gray-50 p-4"
                      >
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                          <div className="flex flex-wrap items-center gap-2">
                            {update.status_from && <StatusBadge status={update.status_from} />}
                            {update.status_from && (
                              <Typography  className="font-semibold text-blue-gray-400">
                                ->
                              </Typography>
                            )}
                            <StatusBadge status={update.status_to} />
                          </div>
                          <Typography  className="text-black">
                            {new Date(update.createdAt).toLocaleString()}
                          </Typography>
                        </div>

                        <Typography className="mt-2 text-black">
                          {update.note || "No note provided."}
                        </Typography>

                        <Typography  className="mt-2 text-black">
                          <span className="font-bold">Updated by:</span> {update.updated_by_name || update.updated_by_bar_number}
                        </Typography>
                      </div>
                    ))
                  )}
                </div>
              </CardBody>
            </Card>
          </>
        )}
      </div>
    </>
  );
}

export default GrievanceDetails;